// Read-only timeline of a deck's edit history. Renders only when the
// deck has more than one version (an unedited deck has v1 only and the
// strip would just be visual noise).
//
// Clicking a non-current version opens an inline panel below the strip
// showing that version's strategy markdown alongside its parsed deck.
// No "Restore" action — this is history, not branching. v1 of the
// versioning feature is read-only.
//
// Renders for both owners (via authed /api/decks/:id/versions) and
// anonymous public viewers (via /api/public/decks/:owner/:slug/versions).
// The component picks the fetch path off `publicOwner`: when set, it
// hits the public route; otherwise the authed route.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  History,
  Minus,
  Plus,
  RefreshCw,
  Sparkles,
  Undo2,
  Upload,
} from "lucide-react";
import {
  getDeckVersion,
  getPublicDeckVersion,
  listDeckVersions,
  listPublicDeckVersions,
  revertDeckToVersion,
  type DeckDiff,
  type DeckVersionListItem,
} from "../lib/api";
import { cn } from "../lib/cn";
import CardHover from "./CardHover";

const SOURCE_LABEL: Record<DeckVersionListItem["source"], string> = {
  build: "build",
  manual_edit: "edit",
  apply_ask: "from ask",
  regenerate: "regen",
  revert: "revert",
  upload: "upload",
};

const SOURCE_ICON: Record<DeckVersionListItem["source"], typeof History> = {
  build: Sparkles,
  manual_edit: History,
  apply_ask: History,
  regenerate: RefreshCw,
  revert: Undo2,
  upload: Upload,
};

interface Props {
  deckId: string;
  /** True only for the deck owner. Drives which fetch path runs and
   *  whether the panel is collapsible by default for public viewers. */
  isOwner: boolean;
  /** When set, the component fetches via the unauthenticated public
   *  route (/api/public/decks/:owner/:slug/versions). When undefined,
   *  it uses the authed /api/decks/:id/versions route. The owner of a
   *  deck always uses the authed path even though their deck might be
   *  public — they have read access either way and the authed path is
   *  consistent with the rest of the owner experience. */
  publicOwner?: string;
}

export default function VersionTimeline({ deckId, isOwner, publicOwner }: Props) {
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);
  // Public mode: anonymous viewer on a public deck. We fetch the
  // versions list but render the strip inside a collapsible <details>
  // so it doesn't compete with Ask for first-touch attention.
  const isPublic = !isOwner && !!publicOwner;
  const enabled = !!deckId && (isOwner || isPublic);

  const listQuery = useQuery({
    queryKey: isPublic
      ? ["public-deck-versions", publicOwner, deckId]
      : ["deck-versions", deckId],
    queryFn: () =>
      isPublic
        ? listPublicDeckVersions(publicOwner!, deckId)
        : listDeckVersions(deckId),
    enabled,
  });

  if (!enabled) return null;
  if (listQuery.isLoading) return null;
  if (listQuery.error) return null;

  const versions = listQuery.data?.versions ?? [];
  // Only render the strip when there's actual history. A deck with
  // just its v1 build row has nothing to navigate to.
  if (versions.length < 2) return null;

  // versions are newest-first; the current version is index 0.
  const current = versions[0]!;

  const strip = (
    <div className="surface p-4 space-y-3">
      <ol className="flex flex-wrap gap-2">
        {/* Render oldest → newest left-to-right so reading order
            follows the deck's evolution. */}
        {[...versions].reverse().map((v) => {
          const isCurrent = v.version_no === current.version_no;
          const isExpanded = v.version_no === expandedVersion;
          const Icon = SOURCE_ICON[v.source];
          return (
            <li key={v.version_no}>
              <button
                type="button"
                onClick={() =>
                  setExpandedVersion(isExpanded ? null : v.version_no)
                }
                className={cn(
                  "inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-xs transition-colors",
                  "border border-border",
                  isCurrent
                    ? "bg-accent/15 text-accent border-accent/30"
                    : "text-fg-muted hover:bg-bg-raised",
                  isExpanded && "ring-1 ring-accent/40",
                )}
                title={
                  new Date(v.created_at).toLocaleString() +
                  " · " +
                  SOURCE_LABEL[v.source] +
                  (v.source === "revert" && v.reverted_from_version_no != null
                    ? ` from v${v.reverted_from_version_no}`
                    : "")
                }
              >
                <span className="font-mono tabular-nums">v{v.version_no}</span>
                <Icon size={11} strokeWidth={2} className="opacity-70" />
                <span className="text-[10px] uppercase tracking-wider opacity-70">
                  {SOURCE_LABEL[v.source]}
                </span>
                {/* For revert rows, show the source version inline so
                    a viewer scanning the strip sees "v8 revert ← v5"
                    without expanding the panel. */}
                {v.source === "revert" && v.reverted_from_version_no != null && (
                  <span className="font-mono tabular-nums text-[10px] opacity-70">
                    ← v{v.reverted_from_version_no}
                  </span>
                )}
                {isCurrent && (
                  <span className="text-[10px] uppercase tracking-wider text-accent">
                    current
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>

      {expandedVersion != null && (
        <ExpandedVersion
          deckId={deckId}
          versionNo={expandedVersion}
          isCurrent={expandedVersion === current.version_no}
          publicOwner={isPublic ? publicOwner : undefined}
          isOwner={isOwner}
          onReverted={() => setExpandedVersion(null)}
        />
      )}
    </div>
  );

  // Public viewers get the timeline collapsed by default — Ask is the
  // primary CTA, version history is supporting context. Owners see it
  // expanded inline so they can scrub through their own iterations.
  if (isPublic) {
    return (
      <section className="space-y-3">
        <details className="group">
          <summary className="cursor-pointer list-none flex items-baseline justify-between hover:text-fg transition-colors">
            <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase group-hover:text-fg">
              Version history
            </h2>
            <span className="text-[11px] text-fg-subtle tabular-nums">
              {versions.length} versions ·{" "}
              <span className="text-fg-muted group-open:hidden">show</span>
              <span className="text-fg-muted hidden group-open:inline">hide</span>
            </span>
          </summary>
          <div className="mt-3">{strip}</div>
        </details>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Version history
        </h2>
        <span className="text-[11px] text-fg-subtle tabular-nums">
          {versions.length} versions
        </span>
      </header>
      {strip}
    </section>
  );
}

function ExpandedVersion({
  deckId,
  versionNo,
  isCurrent,
  publicOwner,
  isOwner,
  onReverted,
}: {
  deckId: string;
  versionNo: number;
  isCurrent: boolean;
  /** When set, fetch via the public-route version-detail endpoint
   *  instead of the authed /api/decks/:id/versions/:n. */
  publicOwner?: string;
  /** True for the deck owner — gates the revert action. */
  isOwner: boolean;
  /** Called after a successful revert so the parent can collapse the
   *  inline panel (the new version becomes 'current' and viewing the
   *  pre-revert version's diff would now be confusing). */
  onReverted: () => void;
}) {
  const isPublic = !!publicOwner;
  const detailQuery = useQuery({
    queryKey: isPublic
      ? ["public-deck-version", publicOwner, deckId, versionNo]
      : ["deck-version", deckId, versionNo],
    queryFn: () =>
      isPublic
        ? getPublicDeckVersion(publicOwner!, deckId, versionNo)
        : getDeckVersion(deckId, versionNo),
    staleTime: Infinity, // versions are immutable once written
  });
  const queryClient = useQueryClient();
  const revertMutation = useMutation({
    mutationFn: () => revertDeckToVersion(deckId, versionNo),
    onSuccess: () => {
      // The deck's current state, version list, and any cached version
      // detail rows can change (a new version row gets written). Drop
      // them all — the next render refetches. Mirrors the keys used by
      // DeckDetail (["deck-detail", ...]) and DeckEdit (["deck", id]).
      queryClient.invalidateQueries({ queryKey: ["deck", deckId] });
      queryClient.invalidateQueries({ queryKey: ["deck-detail"] });
      queryClient.invalidateQueries({ queryKey: ["decks"] });
      queryClient.invalidateQueries({ queryKey: ["deck-versions", deckId] });
      queryClient.invalidateQueries({
        queryKey: ["deck-version", deckId],
      });
      onReverted();
    },
  });

  if (detailQuery.isLoading) {
    return <div className="skeleton h-32 w-full" />;
  }
  if (detailQuery.error) {
    return (
      <div className="text-xs text-danger">
        {(detailQuery.error as Error).message}
      </div>
    );
  }
  const v = detailQuery.data;
  if (!v) return null;

  return (
    <div className="border-t border-border pt-4 space-y-4">
      <div className="flex items-baseline justify-between text-xs text-fg-subtle">
        <span>
          v{v.version_no} ·{" "}
          <span className="capitalize">{v.source.replace("_", " ")}</span>
          {v.source === "revert" && v.reverted_from_version_no != null
            ? ` from v${v.reverted_from_version_no}`
            : ""}
          {v.prompt_version ? ` · prompt ${v.prompt_version}` : ""}
        </span>
        <span className="font-mono tabular-nums">
          {new Date(v.created_at).toLocaleString()}
        </span>
      </div>

      {v.source === "revert" && v.reverted_from_version_no != null && (
        <div className="text-[11px] text-fg-subtle inline-flex items-center gap-1.5">
          <Undo2 size={11} strokeWidth={2} />
          Restored from v{v.reverted_from_version_no}.
        </div>
      )}

      {isCurrent && (
        <div className="text-[11px] text-fg-subtle inline-flex items-center gap-1">
          <Check size={11} strokeWidth={2} />
          This is the version currently shown above.
        </div>
      )}

      {v.version_no === 1 && (
        <div className="text-[11px] text-fg-subtle">
          Initial build. No prior version to diff against.
        </div>
      )}

      {v.diff_from_previous && v.previous_version_no != null && (
        <DiffPanel
          diff={v.diff_from_previous}
          previousVersionNo={v.previous_version_no}
          currentVersionNo={v.version_no}
          revertSlot={
            isOwner && !isCurrent ? (
              <button
                type="button"
                onClick={() => revertMutation.mutate()}
                disabled={revertMutation.isPending}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium",
                  "border border-border text-fg-muted hover:bg-bg-raised hover:text-fg",
                  "transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                )}
                title={`Restore the deck to v${versionNo}'s state. Creates a new version that you can revert again.`}
              >
                <Undo2 size={12} strokeWidth={2} />
                {revertMutation.isPending
                  ? "Reverting…"
                  : `Revert to v${versionNo}`}
              </button>
            ) : null
          }
        />
      )}
      {revertMutation.error && (
        <div className="text-xs text-danger">
          {revertMutation.error instanceof Error
            ? revertMutation.error.message
            : "Revert failed"}
        </div>
      )}
      {/* v1 has no diff_from_previous, but the owner may still want to
          revert to it. Show a standalone Revert button in that case. */}
      {v.version_no === 1 && isOwner && !isCurrent && (
        <button
          type="button"
          onClick={() => revertMutation.mutate()}
          disabled={revertMutation.isPending}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium",
            "border border-border text-fg-muted hover:bg-bg-raised hover:text-fg",
            "transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          <Undo2 size={12} strokeWidth={2} />
          {revertMutation.isPending ? "Reverting…" : "Revert to v1"}
        </button>
      )}
    </div>
  );
}

// Renders the card-list diff between two consecutive versions. Three
// row types: removed (red), added (green), changed-count (yellow).
// Section is shown next to the name so a card moving between mainboard
// and sideboard reads as one remove + one add (which mirrors how the
// underlying diff function describes such moves — see deck-diff.ts).
function DiffPanel({
  diff,
  previousVersionNo,
  currentVersionNo,
  revertSlot,
}: {
  diff: DeckDiff;
  previousVersionNo: number;
  currentVersionNo: number;
  /** Optional action button slotted into the diff header (top-right).
   *  Currently used for the Revert button. Rendered next to the diff
   *  count so it stays in the natural eye line for owners. */
  revertSlot?: React.ReactNode;
}) {
  const total =
    diff.added.length + diff.removed.length + diff.changed.length;
  if (total === 0) {
    return (
      <div className="space-y-2">
        <div className="text-[11px] text-fg-subtle">
          No card changes between v{previousVersionNo} and v{currentVersionNo}.
        </div>
        {revertSlot}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <header className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-fg-subtle">
          Diff vs v{previousVersionNo}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-fg-subtle tabular-nums">
            {total} change{total === 1 ? "" : "s"}
          </span>
          {revertSlot}
        </div>
      </header>
      <ul className="rounded-md border border-border/60 bg-bg-surface/30 divide-y divide-border/40 text-sm">
        {diff.removed.map((r) => (
          <li
            key={`r-${r.section}-${r.name}`}
            className="flex items-center gap-2 px-3 py-1.5"
          >
            <Minus size={13} strokeWidth={2} className="text-danger shrink-0" />
            <span className="font-mono text-xs tabular-nums text-danger w-6 text-right">
              {r.count}
            </span>
            <span className="flex-1 text-danger/90 line-through">
              <CardHover name={r.name}>{r.name}</CardHover>
            </span>
            <SectionTag section={r.section} />
          </li>
        ))}
        {diff.added.map((a) => (
          <li
            key={`a-${a.section}-${a.name}`}
            className="flex items-center gap-2 px-3 py-1.5"
          >
            <Plus size={13} strokeWidth={2} className="text-accent shrink-0" />
            <span className="font-mono text-xs tabular-nums text-accent w-6 text-right">
              {a.count}
            </span>
            <span className="flex-1 text-accent">
              <CardHover name={a.name}>{a.name}</CardHover>
            </span>
            <SectionTag section={a.section} />
          </li>
        ))}
        {diff.changed.map((c) => (
          <li
            key={`c-${c.section}-${c.name}`}
            className="flex items-center gap-2 px-3 py-1.5"
          >
            <ArrowRight
              size={13}
              strokeWidth={2}
              className="text-warn shrink-0"
            />
            <span className="font-mono text-xs tabular-nums text-warn w-auto">
              {c.from} → {c.to}
            </span>
            <span className="flex-1 text-warn/90">
              <CardHover name={c.name}>{c.name}</CardHover>
            </span>
            <SectionTag section={c.section} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function SectionTag({ section }: { section: "main" | "side" }) {
  return (
    <span className="text-[10px] uppercase tracking-wider text-fg-subtle font-mono shrink-0">
      {section === "main" ? "main" : "side"}
    </span>
  );
}
